import { useMutation } from "@apollo/client"
import { HeartIcon } from "@heroicons/react/24/outline"
import clsx from "clsx"
import { Text } from "@/elements"
import { useQuery } from "@apollo/client"
import { useCallback, useEffect, useRef, useState } from "react"
import { graphql } from "@/gql"
import { ampli } from "@/ampli"
import { decodeGraphqlNodeId } from "@/tracking"
import { LikeCreativeWorkPortfolioProjectDocument, UnlikeCreativeWorkPortfolioProjectDocument } from "@/gql/graphql"

const LikeCreativeWorkPortfolioProject = graphql(`
  mutation LikeCreativeWorkPortfolioProject($id: ID!) {
    likeCreativeWorkPortfolioProject(id: $id) {
      ok
      like {
        id
        project {
          id
          viewerHasLiked
          likeCount
          likeCreativePreviews {
            id
            name
            avatarInitials
            avatarUrl(width: 80)
          }
        }
      }
    }
  }
`)

const UnlikeCreativeWorkPortfolioProject = graphql(`
  mutation UnlikeCreativeWorkPortfolioProject($id: ID!) {
    unlikeCreativeWorkPortfolioProject(id: $id) {
      project {
        id
        viewerHasLiked
        likeCount
        likeCreativePreviews {
          id
          name
          avatarInitials
          avatarUrl(width: 80)
        }
      }
    }
  }
`)

const LikeProjectQuery = graphql(`
  query LikeProject($projectId: ID!) {
    creativeWorkPortfolioProject(id: $projectId) {
      id
      viewerHasLiked
    }
  }
`)

interface Props {
  projectId: string
  entryPoint: string
}

export const LikeProject: React.FC<Props> = ({ projectId, entryPoint }) => {
  const { data, loading } = useQuery(LikeProjectQuery, {
    variables: {
      projectId,
    },
    context: {
      version: 2,
    },
  })
  const [likeMutation] = useMutation(LikeCreativeWorkPortfolioProjectDocument)
  const [unlikeMutation] = useMutation(UnlikeCreativeWorkPortfolioProjectDocument)
  const [isLiked, setIsLiked] = useState<boolean>(false)

  const debounceTimeoutRef = useRef<number | null>(null)
  const initialIsLiked = useRef(isLiked)

  useEffect(() => {
    if (!loading && data?.creativeWorkPortfolioProject) {
      setIsLiked(data.creativeWorkPortfolioProject.viewerHasLiked!)
      initialIsLiked.current = data.creativeWorkPortfolioProject.viewerHasLiked!
    }
  }, [data, loading])

  const toggleLike = useCallback(
    (like: boolean) => {
      // Update state immediately
      setIsLiked(like)

      // Clear any pending timeout
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current)
      }

      debounceTimeoutRef.current = window.setTimeout(async () => {
        // Check if the current state differs from the initial state
        if (like !== initialIsLiked.current) {
          if (like) {
            await likeMutation({ variables: { id: projectId! } })
            ampli.projectLike({
              project_id: decodeGraphqlNodeId(projectId).numericalId || "",
              entry_point: entryPoint,
            })
          } else {
            await unlikeMutation({ variables: { id: projectId! } })
          }
          // Update the initial state to reflect the current state
          initialIsLiked.current = like
        }
        debounceTimeoutRef.current = null // Reset the timeout ref
      }, 300)
    },
    [likeMutation, unlikeMutation, projectId, entryPoint]
  )

  const onLike = () => {
    if (!loading) {
      toggleLike(!isLiked)
    }
  }

  return (
    <div className={clsx("group flex flex-row items-center gap-2", !loading && "cursor-pointer")} onClick={onLike}>
      <HeartIcon
        className={clsx(
          "size-[22px]",
          loading
            ? "fill-bg-2 stroke-bg-2"
            : isLiked
              ? "fill-fg-1 stroke-fg-1 group-hover:fill-bg-1"
              : "stroke-fg-1 group-hover:fill-fg-1"
        )}
      />
      <Text className={clsx(loading ? "text-bg-2" : "text-fg-1")}>Like</Text>
    </div>
  )
}
